import { AccountSettings } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import Copyright from "03AdminPages/99Parts/Copyright";
import { NoteText, PasswordPolicy } from "97Parts/Styles";
import { useSignal } from "99Tools/useModel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { validate } from "email-validator";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

const MyPaper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const MyAvatar = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const MyForm = styled("form")(({ theme }) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: theme.spacing(1),
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const ResetPage = () => {
  const a = useContext(AppContext).account;
  const navigate = useNavigate();
  const [account] = useState<AccountSettings>(new AccountSettings());
  const [showPwd, setShowPwd] = useState(false);
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [message, setMessage] = useState("");
  const redraw = useSignal();

  const handleSubmit = () => {
    if (account.user === "") {
      setMessage("メールアドレスを入力してください。");
      return;
    }
    if (!validate(account.user)) {
      setMessage("有効なメールアドレスを入力してください。");
      return;
    }
    if (account.pass === "") {
      setMessage("パスワードを入力してください。");
      return;
    }
    if (account.passConfirm === "") {
      setMessage("確認用パスワードを入力してください。");
      return;
    }
    if (account.pass !== account.passConfirm) {
      setMessage("パスワードが一致しません。");
      return;
    }
    if (
      account.pass.match(/^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d).{8,}$/) == null
    ) {
      setMessage(
        "パスワードは8文字以上で大文字・小文字・数字をそれぞれ含む必要があります。"
      );
      return;
    }
    a.ForgotPassword(account)
      .then(() => {
        setStep("confirm");
      })
      .catch((err: any) => {
        setMessage(
          "パスワードリセットエラーが発生しました。管理者に連絡してください。"
        );
        console.log("パスワードリセットエラー");
        console.error(err);
      });
  };

  const handleConfirm = () => {
    if (account.code === "") {
      setMessage("コードを入力してください。");
      return;
    }
    a.ForgotPasswordSubmit(account)
      .then((err: any) => {
        navigate("/login/");
      })
      .catch((err: any) => {
        setMessage(
          "コードが誤っているか有効期限切れです。訂正して再送するか新しいコードを発行してください。"
        );
        console.log("コード確認エラー");
        console.error(err);
      });
  };

  const handleChangeMail = (e: any) => {
    account.user = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangePass = (e: any) => {
    account.pass = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangePassConfirm = (e: any) => {
    account.passConfirm = e.target.value;
    setMessage("");
    redraw();
  };

  const handleShowPwd = (e: any) => {
    setMessage("");
    setShowPwd(e.target.checked);
  };

  const handleChangeCode = (e: any) => {
    account.code = e.target.value;
    setMessage("");
    redraw();
  };

  const form = (
    <>
      <Typography component="h1" variant="h5">
        パスワードリセット
      </Typography>
      <MyForm noValidate>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          autoFocus
          id="email"
          label="メールアドレス"
          name="email"
          autoComplete="email"
          inputProps={{ maxLength: 128 }}
          value={account.user}
          onChange={handleChangeMail}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <TextField
            sx={{ width: "48%" }}
            variant="outlined"
            margin="normal"
            required
            name="password"
            label="パスワード"
            autoComplete="new-password"
            type={showPwd ? "text" : "password"}
            id="password"
            inputProps={{ maxLength: 128 }}
            value={account.pass}
            onChange={handleChangePass}
          />
          <TextField
            sx={{ width: "48%" }}
            variant="outlined"
            margin="normal"
            required
            name="password"
            label="確認用パスワード"
            autoComplete="new-password"
            type={showPwd ? "text" : "password"}
            id="passwordConfirm"
            inputProps={{ maxLength: 128 }}
            value={account.passConfirm}
            onChange={handleChangePassConfirm}
          />
        </div>
        <PasswordPolicy>
          ※半角英大文字・半角英字小文字・数字を含む、８文字以上のパスワードを設定してください。
        </PasswordPolicy>
        <FormControlLabel
          control={<Checkbox onChange={handleShowPwd} />}
          label="パスワードを表示します"
        />
        {0 < message.length ? <ErrorText>{message}</ErrorText> : null}
        <SubmitButton
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
        >
          次へ
        </SubmitButton>
      </MyForm>
    </>
  );

  const confirm = (
    <>
      <Typography component="h1" variant="h5">
        メールアドレスの確認
      </Typography>
      <MyForm noValidate>
        <NoteText>{`${account.user}宛にお送りした認証コードを入力ください。見つからない場合は、迷惑メールフォルダをご確認ください`}</NoteText>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="code"
          label="コードを入力"
          name="code"
          autoComplete="off"
          inputProps={{ maxLength: 128 }}
          value={account.code}
          onChange={handleChangeCode}
        />
        {0 < message.length ? <ErrorText>{message}</ErrorText> : null}
        <SubmitButton
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => handleConfirm()}
        >
          登録
        </SubmitButton>
      </MyForm>
    </>
  );

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <MyPaper>
        <Typography component="h1" variant="h5">
          漏水みはり番監視システム
        </Typography>
        <MyAvatar>
          <LockOutlinedIcon />
        </MyAvatar>
        {step === "form" ? form : confirm}
      </MyPaper>
      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
};

export default ResetPage;
